import { LinkProps } from "@mui/material";
import {
  FooterProps,
  RacwaBreadcrumbDetails,
  RacwaResponsiveHeaderProps,
  RacwaStepperTemplateProps,
} from "@racwa/react-components";
import { EMPTY_URL, InsurancePhoneNumber, PhoneButton } from "raci-react-library";
import { useRecoilValue } from "recoil";
import { routeStateAtom } from "../../atoms";
import { getPageTitle } from "../../constants";
import { HeaderButtons, TestBanner } from "raci-react-library-npe";
import { EnvironmentHealthStatus } from "../../../__NPE__/EnvironmentHealthStatus";
import getB2CPCMUrl from "../../utils/getB2CPCMUrl";

type CommonPageProps = Pick<
  RacwaStepperTemplateProps,
  | "breadcrumbs"
  | "responsiveHeaderProps"
  | "headerEndAction"
  | "notificationBannerContent"
  | "showNotificationBanner"
  | "footerProps"
>;

const getLinkProps = (link?: string): Partial<LinkProps> => ({
  href: link ?? EMPTY_URL,
});

export const getBreadcrumbs = (policyNumber: string, isMidterm?: boolean) => {
  return {
    links: [
      { name: "myRAC", href: import.meta.env.VITE_RAC_HOME_PAGE || "", key: "breadcrumbMyRac" },
      {
        name: `Car insurance ${policyNumber}`,
        href: getB2CPCMUrl(policyNumber),
        key: "breadcrumbPolicyNumber",
      },
    ],
    currentPage: { name: getPageTitle(isMidterm) },
  };
};

export const usePage = (): CommonPageProps => {
  const { policyNumber, isMidterm } = useRecoilValue(routeStateAtom);
  const breadcrumbs: RacwaBreadcrumbDetails = getBreadcrumbs(policyNumber ?? "", isMidterm);

  const responsiveHeaderProps: RacwaResponsiveHeaderProps = {
    NavBreadcrumbsProps: {
      homeLink: import.meta.env.VITE_RAC_HOME_PAGE ?? EMPTY_URL,
    },
  };

  const footerProps: FooterProps = {
    variant: "sidebar",
    privacyLinkProps: getLinkProps(import.meta.env.VITE_RAC_ABOUT_PRIVACY_PAGE),
    securityLinkProps: getLinkProps(import.meta.env.VITE_RAC_ABOUT_SECURITY_PAGE),
    disclaimerLinkProps: getLinkProps(import.meta.env.VITE_RAC_ABOUT_DISCLAIMER_PAGE),
    accessibilityLinkProps: getLinkProps(import.meta.env.VITE_RAC_ABOUT_ACCESSIBILITY_PAGE),
  };

  let notificationBannerContent;

  const phoneButton = <PhoneButton phone={InsurancePhoneNumber} id={"header-phone-button"} />;
  let headerEndAction = phoneButton;

  /*
   ** WARNING:
   ** KNOW WHAT YOU'RE DOING BEFORE MAKING CHANGES TO TO THE BELOW IF STATEMENT
   ** UPON A BUILD, VITE WILL REMOVE CODE IN THE OUTPUTTED JAVASCRIPT FILE DEPENDING ON THE TARGET ENVIRONMENT
   ** THIS MEANS THAT FOR LOCAL/DEV/SIT BUILDS WILL INCLUDE AND SHIP CODE
   ** (YOU CAN SEE AND SEARCH FOR IN OUTPUTTED JAVASCRIPT ARTIFACTS)
   */
  if (
    import.meta.env.VITE_ENVIRONMENT === "LOCAL" ||
    import.meta.env.VITE_ENVIRONMENT === "DEV" ||
    import.meta.env.VITE_ENVIRONMENT === "SIT" ||
    import.meta.env.VITE_ENVIRONMENT === "UAT"
  ) {


    notificationBannerContent = <TestBanner />;

    headerEndAction = (
      <HeaderButtons
        phoneButton={phoneButton}
        npeButtons={[<EnvironmentHealthStatus />]}
        isUat={import.meta.env.VITE_ENVIRONMENT === "UAT"}
        appConfig={{ showOverrideToNumberSelector: true }}
      />
    );
  }

  return {
    breadcrumbs,
    responsiveHeaderProps,
    headerEndAction,
    notificationBannerContent,
    showNotificationBanner: !!notificationBannerContent,
    footerProps,
  };
};

export default usePage;
